// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: one note on the "What Konvo knows" page. At rest: the text and a small source icon. On
// hover (desktop): where it came from and when, Change and Forget. Change edits in place (Enter
// saves, Escape cancels) with a Move to menu; right click opens the same choices. On the phone the
// row is a button that opens the note sheet.

import {
	forgetKnowsNote,
	noteMenuGroups,
	topicRadios,
	undoForgetKnowsNote,
	updateKnowsNote,
} from '@app/features/konvo_knows/components/KnowsActions';
import {M, noteOriginText} from '@app/features/konvo_knows/components/KnowsMessages';
import styles from '@app/features/konvo_knows/components/KonvoKnows.module.css';
import KonvoKnows from '@app/features/konvo_knows/state/KonvoKnows';
import {displayTopic, type KnowsNote, nameHue, parsePersonNote} from '@app/features/konvo_knows/utils/KnowsModel';
import {DataMenuRenderer} from '@app/features/ui/action_menu/DataMenuRenderer';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import {
	ArrowCounterClockwiseIcon,
	ChatCircleIcon,
	CheckIcon,
	ClockCounterClockwiseIcon,
	FolderSimpleIcon,
	PencilSimpleIcon,
	TrashIcon,
	UserIcon,
	XIcon,
} from '@app/features/ui/icons/Icons';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useLayoutEffect, useRef, useState} from 'react';

export function SourceIcon({note, size = 14}: {note: KnowsNote; size?: number}) {
	switch (note.source) {
		case 'chat':
			return <ChatCircleIcon size={size} />;
		case 'you':
			return <UserIcon size={size} />;
		case 'legacy':
			return <ClockCounterClockwiseIcon size={size} />;
	}
}

/** "Name: role" in the People card as an initials avatar, the name and the role; else the text. */
export function NoteText({note, asPerson}: {note: KnowsNote; asPerson: boolean}) {
	const person = asPerson ? parsePersonNote(note.text) : null;
	if (!person) return <span className={styles.noteText}>{note.text}</span>;
	return (
		<>
			<span
				className={styles.personAvatar}
				style={{'--kk-hue': nameHue(person.name)} as React.CSSProperties}
				aria-hidden="true"
			>
				{person.initials}
			</span>
			<span className={styles.personText}>
				<b>{person.name}</b>
				<small>{person.role}</small>
			</span>
		</>
	);
}

function RowIconButton({
	label,
	icon,
	onClick,
	danger,
	flx,
}: {
	label: string;
	icon: React.ReactNode;
	onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
	danger?: boolean;
	flx: string;
}) {
	return (
		<Tooltip text={label}>
			<button
				type="button"
				className={clsx(styles.rowButton, danger && styles.rowButtonDanger)}
				aria-label={label}
				onClick={onClick}
				data-flx={flx}
			>
				{icon}
			</button>
		</Tooltip>
	);
}

function NoteEditor({note, onDone}: {note: KnowsNote; onDone: () => void}) {
	const {i18n} = useLingui();
	const [text, setText] = useState(note.text);
	const ref = useRef<HTMLTextAreaElement>(null);
	useLayoutEffect(() => {
		const area = ref.current;
		if (!area) return;
		area.style.height = '0px';
		area.style.height = `${area.scrollHeight}px`;
	}, [text]);
	useLayoutEffect(() => {
		const area = ref.current;
		if (!area) return;
		area.focus({preventScroll: true});
		area.setSelectionRange(area.value.length, area.value.length);
		// Opened from the corner card's Change: the note may be below the fold.
		area.scrollIntoView({block: 'nearest', behavior: 'smooth'});
	}, []);
	const save = async () => {
		if (!text.trim()) return;
		if (await updateKnowsNote(i18n, note, {text})) onDone();
	};
	const openMove = (event: React.MouseEvent<HTMLButtonElement>) =>
		ContextMenuCommands.openFromElementBottomRight(event, ({onClose}) => (
			<DataMenuRenderer
				groups={[
					{
						items: topicRadios(i18n, displayTopic(note), (topic) => {
							onClose();
							void updateKnowsNote(i18n, note, {text, topic}).then((ok) => ok && onDone());
						}),
					},
				]}
			/>
		));
	return (
		<div className={clsx(styles.row, styles.rowEditing)} data-flx="konvo-knows.note.editing">
			<textarea
				ref={ref}
				className={styles.editInput}
				value={text}
				rows={1}
				maxLength={300}
				aria-label={i18n._(M.change)}
				onChange={(event) => setText(event.target.value.replace(/\n/g, ' '))}
				onKeyDown={(event) => {
					if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
						event.preventDefault();
						void save();
					} else if (event.key === 'Escape') {
						event.preventDefault();
						event.stopPropagation();
						onDone();
					}
				}}
				data-flx="konvo-knows.note.edit-input"
			/>
			<span className={styles.rowActions}>
				<RowIconButton
					label={i18n._(M.moveTo)}
					icon={<FolderSimpleIcon size={15} />}
					onClick={openMove}
					flx="konvo-knows.note.move"
				/>
				<RowIconButton
					label={i18n._(M.cancel)}
					icon={<XIcon size={15} />}
					onClick={onDone}
					flx="konvo-knows.note.cancel"
				/>
				<RowIconButton
					label={i18n._(M.save)}
					icon={<CheckIcon size={15} />}
					onClick={() => void save()}
					flx="konvo-knows.note.save"
				/>
			</span>
		</div>
	);
}

export const KnowsNoteRow = observer(({note, asPerson}: {note: KnowsNote; asPerson: boolean}) => {
	const {i18n} = useLingui();
	const setEditing = (on: boolean) => KonvoKnows.setEditing(on ? note.id : null);
	if (KonvoKnows.editingId === note.id) return <NoteEditor note={note} onDone={() => setEditing(false)} />;
	const origin = noteOriginText(i18n, note);
	const openMenu = (event: React.MouseEvent) =>
		ContextMenuCommands.openFromEvent(event, ({onClose}) => (
			<DataMenuRenderer
				groups={noteMenuGroups(
					i18n,
					note,
					{onChange: () => setEditing(true), close: onClose},
					{
						change: <PencilSimpleIcon size={18} />,
						move: <FolderSimpleIcon size={18} />,
						forget: <TrashIcon size={18} />,
					},
				)}
			/>
		));
	const person = asPerson && parsePersonNote(note.text) != null;
	return (
		<div
			className={clsx(styles.row, styles.hoverable, person && styles.rowPerson)}
			role="group"
			aria-label={note.text}
			onContextMenu={openMenu}
			data-flx="konvo-knows.note"
		>
			<NoteText note={note} asPerson={asPerson} />
			<span className={styles.rowRest}>
				<span className={styles.rowSource} title={origin}>
					<SourceIcon note={note} size={13} />
				</span>
				<span className={styles.rowHover}>
					<span className={styles.rowMeta}>
						<SourceIcon note={note} size={13} />
						{origin}
					</span>
					<RowIconButton
						label={i18n._(M.change)}
						icon={<PencilSimpleIcon size={15} />}
						onClick={() => setEditing(true)}
						flx="konvo-knows.note.change"
					/>
					<RowIconButton
						label={i18n._(M.forget)}
						icon={<TrashIcon size={15} />}
						onClick={() => void forgetKnowsNote(i18n, note)}
						flx="konvo-knows.note.forget"
					/>
				</span>
			</span>
		</div>
	);
});

/** Where a note was until a few seconds ago: "Forgotten" and Undo. */
export function ForgottenRow({note}: {note: KnowsNote}) {
	const {i18n} = useLingui();
	const [busy, setBusy] = useState(false);
	return (
		<div className={clsx(styles.row, styles.forgottenRow)} data-flx="konvo-knows.note.forgotten">
			<TrashIcon size={14} className={styles.forgottenIcon} />
			<span className={styles.forgottenText}>
				<span>{i18n._(M.forgotten)}</span>
				<s>{note.text}</s>
			</span>
			<button
				type="button"
				className={styles.pill}
				disabled={busy}
				onClick={() => {
					setBusy(true);
					void undoForgetKnowsNote(i18n, note);
				}}
				data-flx="konvo-knows.forget.undo"
			>
				<ArrowCounterClockwiseIcon size={14} />
				{i18n._(M.undo)}
			</button>
		</div>
	);
}

/** Phone: the whole row opens the note sheet. */
export function PhoneNoteRow({note, asPerson, onOpen}: {note: KnowsNote; asPerson: boolean; onOpen: () => void}) {
	return (
		<button
			type="button"
			className={clsx(styles.row, styles.phoneRow, asPerson && parsePersonNote(note.text) && styles.rowPerson)}
			onClick={onOpen}
			data-flx="konvo-knows.note"
		>
			<NoteText note={note} asPerson={asPerson} />
			<span className={styles.rowSourceStatic} aria-hidden="true">
				<SourceIcon note={note} size={14} />
			</span>
		</button>
	);
}
